import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/features/auth'

interface AdminStats {
  totalPatients: number
  activePatients: number
  totalProfessionals: number
  activeProfessionals: number
  totalReports: number
  reportsThisMonth: number
  totalInsights: number
  pendingInsights: number
}

async function fetchAdminStats(organizationId: string): Promise<AdminStats> {
  const monthAgo = new Date()
  monthAgo.setMonth(monthAgo.getMonth() - 1)

  const [
    patientsCount,
    activePatientsCount,
    professionalsCount,
    activeProfessionalsCount,
    reportsCount,
    reportsThisMonth,
    insightsCount,
    pendingInsightsCount,
  ] = await Promise.all([
    supabase
      .from('patients')
      .select('id', { count: 'exact', head: true })
      .eq('organization_id', organizationId)
      .neq('status', 'deleted'),
    supabase
      .from('patients')
      .select('id', { count: 'exact', head: true })
      .eq('organization_id', organizationId)
      .eq('status', 'active'),
    // Count organization members (professionals)
    supabase
      .from('organization_members')
      .select('profile_id', { count: 'exact', head: true })
      .eq('organization_id', organizationId),
    supabase
      .from('organization_members')
      .select('profile_id', { count: 'exact', head: true })
      .eq('organization_id', organizationId)
      .eq('is_active', true),
    supabase
      .from('daily_reports')
      .select('id, patients!inner(organization_id, status)', { count: 'exact', head: true })
      .eq('patients.organization_id', organizationId)
      .neq('patients.status', 'deleted'),
    supabase
      .from('daily_reports')
      .select('id, patients!inner(organization_id, status)', { count: 'exact', head: true })
      .eq('patients.organization_id', organizationId)
      .neq('patients.status', 'deleted')
      .gte('created_at', monthAgo.toISOString()),
    supabase
      .from('key_insights')
      .select('id, patients!inner(organization_id, status)', { count: 'exact', head: true })
      .eq('patients.organization_id', organizationId)
      .neq('patients.status', 'deleted'),
    supabase
      .from('key_insights')
      .select('id, patients!inner(organization_id, status)', { count: 'exact', head: true })
      .eq('patients.organization_id', organizationId)
      .neq('patients.status', 'deleted')
      .is('acknowledged_at', null),
  ])

  return {
    totalPatients: patientsCount.count ?? 0,
    activePatients: activePatientsCount.count ?? 0,
    totalProfessionals: professionalsCount.count ?? 0,
    activeProfessionals: activeProfessionalsCount.count ?? 0,
    totalReports: reportsCount.count ?? 0,
    reportsThisMonth: reportsThisMonth.count ?? 0,
    totalInsights: insightsCount.count ?? 0,
    pendingInsights: pendingInsightsCount.count ?? 0,
  }
}

export function useAdminStats() {
  const { currentOrganization } = useAuth()
  const organizationId = currentOrganization?.id

  return useQuery({
    queryKey: ['admin-stats', organizationId],
    queryFn: () => fetchAdminStats(organizationId!),
    enabled: !!organizationId,
  })
}
